import { injectIntl } from "react-intl";
import React, { useEffect, useState, useRef } from "react";
import { isValidArray } from "helpers/validation";
import ColorPicker from "components/ColorPicker";
import text from "strings/text";
import { defaultColor } from "constants/tagColors";
import { Mixpanel } from "helpers/Mixpanel";
import "./DetailFieldRoleComponent.scss";

export function DetailFieldRoleComponent({ roles, selectRoles, roleDisplayText, disableField, selectedRole, onColorSelect, onCreateRole }) {
    const [isCreatingRole, setIsCreatingRole] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [newRoleColor, setNewRoleColor] = useState(defaultColor);
    const [isNoRoleSelected, setIsNoRoleSelected] = useState(false);
    const [roleNameError, setRoleNameError] = useState('');
    const roleInputRef = useRef(null);

    useEffect(() => {
        console.log('DetailFieldRoleComponent - selectedRole changed:', selectedRole);
    }, [selectedRole]);

    const validateRoleName = (roleName) => {
        if (!roleName || !roleName.trim()) {
            setRoleNameError(text.roleNameEmptyError);
            return false;
        }

        // Check for duplicate role names (case insensitive)
        const trimmedRoleName = roleName.trim();
        const existingRole = roles?.find(role => 
            role.name.toLowerCase() === trimmedRoleName.toLowerCase()
        );

        if (existingRole) {
            setRoleNameError(text.roleNameConflictError);
            return false;
        }

        setRoleNameError('');
        return true;
    };

    const handleNewRoleNameChange = (e) => {
        const value = e.target.value;
        setNewRoleName(value);
        
        // Clear error when user starts typing
        if (roleNameError) {
            setRoleNameError('');
        }
    };

    const handleNoRoleClick = () => {
        setIsNoRoleSelected(true);
        selectRoles([]);
        const eventProps = selectedRole
            ? { role_id: selectedRole.id, role_name: selectedRole.name }
            : undefined;
        Mixpanel.track('Remove role from shift', eventProps);
    };

    const handleSelectRole = (role) => {
        selectRoles([role]);
        setIsNoRoleSelected(false);
        Mixpanel.track('Add role to shift', { role_id: role.id, role_name: role.name });
    };

    const handleAutoSelectRoleAfterCreation = (role) => {
        handleSelectRole(role);
        setIsCreatingRole(false);
    }

    const handleCreateRoleClick = () => {
        setIsCreatingRole(true);
        setRoleNameError(''); // Clear any previous errors
        // Use setTimeout to ensure the DOM has been updated before focusing
        setTimeout(() => {
            if (roleInputRef.current) {
                roleInputRef.current.focus();
            }
        }, 0);
    };

    const handleCancelCreate = () => {
        setIsCreatingRole(false);
        setNewRoleName('');
        setNewRoleColor(defaultColor);
        setRoleNameError('');
    };

    const handleCreateRole = () => {
        if (validateRoleName(newRoleName) && onCreateRole) {
            const roleName = newRoleName.trim();
            Mixpanel.track('Create role from shift', { role_name: roleName, color_code: newRoleColor });
            onCreateRole(roleName, newRoleColor, handleAutoSelectRoleAfterCreation);
        }
    };

    const handleNewRoleColorSelect = (color) => {
        setNewRoleColor(color);
    };

    const roleList = () => {
        if (isValidArray(roles) === false) {
            return null;
        }

        const sortedRoles = [...roles].sort((a, b) => {
            if (a.name > b.name) return 1;
            if (a.name < b.name) return -1;
            return 0;
        });

        return sortedRoles.map((element) => {
            return (
                <li className='dropdown-li' key={element.id} onClick={() => handleSelectRole(element)}>
                    <div className="role-option-content">
                        <span 
                            className="role-color-dot" 
                            style={{ backgroundColor: `#${element.color_code || defaultColor}` }}
                        />
                        <label>{element.name}</label>
                    </div>
                </li>
            );
        });
    }

    // Get the color of the selected role, default to defaultColor if no role is selected
    const getSelectedRoleColor = () => {
        if (selectedRole && selectedRole.color_code) {
            return selectedRole.color_code;
        }
        return defaultColor;
    };

    const selectedColor = getSelectedRoleColor();

    const renderTitle = () => (
        <div className="title-label">{text.role}</div>
    );

    const renderColorPicker = (color, onSelect) => (
        <ColorPicker 
            key={`${selectedRole?.id}-${color}`}
            selectedColor={color}
            onColorSelect={onSelect}
            disabled={disableField}
        />
    );

    const renderCreateRoleForm = () => (
        <div className="detail-field-role">
            {renderTitle()}
            <div className="role-controls">
                <div className={`role-input-container ${roleNameError ? 'error' : ''}`}>
                    <input
                        ref={roleInputRef}
                        type="text"
                        placeholder=''
                        value={newRoleName}
                        onChange={handleNewRoleNameChange}
                        className={`role-input ${roleNameError ? 'error' : ''}`}
                    />
                    
                    <button
                        onClick={handleCreateRole}
                        disabled={!newRoleName.trim() || roleNameError}
                        className={`create-role-button ${newRoleName.trim() && !roleNameError ? 'enabled' : 'disabled'}`}
                    >
                        <img src="/icons/create_check.svg" alt="create-check" />
                    </button>
                </div>

                <button
                    onClick={handleCancelCreate}
                    className="exit-create-mode"
                >
                    <img src="/icons/close-comments.svg" alt="cancel-check" />
                </button>

                {renderColorPicker(newRoleColor, handleNewRoleColorSelect)}
            </div>
            {roleNameError && (
                <div className="error-message">
                    {roleNameError}
                </div>
            )}
        </div>
    );

    const renderDropdownOptions = () => (
        <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton4">
            <li className='dropdown-li no-role-option' onClick={handleNoRoleClick}>
                <div className="role-option-content">
                    <span className="role-color-dot" />
                    <label>{text.noRole}</label>
                </div>
            </li>
            <li className='dropdown-li create-role-option' onClick={handleCreateRoleClick}>
                <div className="role-option-content">
                    <span className="role-color-dot" />
                    <label>{text.createRoleOption}</label>
                </div>
            </li>
            <li className="dropdown-divider"></li>
            {roleList()}
        </ul>
    );

    const shouldShowColorPicker = isCreatingRole || selectedRole;

    const renderNormalMode = () => (
        <div className="detail-field-role">
            {renderTitle()}
            <div className="role-controls">
                <button 
                    disabled={disableField} 
                    id="dropdownMenuButton4" 
                    className={`header-button role-header role-placeholder`} 
                    type="button" 
                    data-bs-toggle="dropdown" 
                    aria-expanded="false"
                    style={{ width: shouldShowColorPicker ? '326px' : '100%' }}
                >
                    {  isCreatingRole ? newRoleName : (selectedRole ? roleDisplayText : (isNoRoleSelected ? text.noRole : text.selectRole) )  }
                     
                </button>
                
                {shouldShowColorPicker && renderColorPicker(selectedColor, onColorSelect)}

                {renderDropdownOptions()}
            </div>
        </div>
    );

    return isCreatingRole ? renderCreateRoleForm() : renderNormalMode();
}

export default injectIntl(DetailFieldRoleComponent); 